import { AlertFactory } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import {
  action,
  computed,
  makeObservable,
  observable,
  override,
  runInAction,
} from 'mobx'
import { task } from 'mobx-task'
import Job from 'jobs/Job'
import IndividualProfileStore from 'jobs/info-gathering/individual-info/IndividualProfileStore'
import PersonalProfileViewModel from 'jobs/info-gathering/individual-info/PersonalProfile/PersonalProfileViewModel'
import SpousalProfileViewModel from 'jobs/info-gathering/individual-info/SpousalProfile/SpousalProfileViewModel'
import DependentProfileViewModel from 'jobs/info-gathering/individual-info/DependentProfile/DependentProfileViewModel'
import InfoGatheringStore from 'jobs/InfoGatheringStore'
import PersonalProfileEditorViewModel from 'jobs/info-gathering/individual-info/editors/PersonalProfileEditorViewModel'
import SpousalProfileEditorViewModel from 'jobs/info-gathering/individual-info/editors/SpousalProfileEditorViewModel'
import DependentProfileEditorViewModel from 'jobs/info-gathering/individual-info/editors/DependentProfileEditorViewModel'

export type ModalTab = 'personal' | 'spousal' | 'dependents'

export const TAB_LABELS: Record<ModalTab, string> = {
  personal: 'Personal',
  spousal: 'Spouse',
  dependents: 'Dependents',
}

export default class EditPersonalProfileDialogViewModel extends ModalViewModel {
  readonly personalProfileEditorVm = new PersonalProfileEditorViewModel()

  readonly spousalProfileEditorVm = new SpousalProfileEditorViewModel()

  @observable activeTab: ModalTab = 'personal'

  @observable dependentProfileEditors: DependentProfileEditorViewModel[] = []

  @observable pendingDelistIds: string[] = []

  constructor(
    private job: Job,
    private personalProfileVM: PersonalProfileViewModel,
    private spousalProfileVM: SpousalProfileViewModel,
    private dependentProfileVM: DependentProfileViewModel,
    private individualProfileStore: IndividualProfileStore,
    private infoGatheringStore: InfoGatheringStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  @action.bound
  validate() {
    switch (this.activeTab) {
      case 'personal':
        return this.personalProfileEditorVm.validate()
      case 'spousal':
        return this.spousalProfileEditorVm.validate()
      case 'dependents':
        return this.dependentProfileEditors
          .map((e) => e.validate())
          .every(Boolean)
    }
  }

  @action.bound
  addDependent() {
    this.dependentProfileEditors.push(new DependentProfileEditorViewModel())
  }

  @action.bound
  removeDependentAt(index: number) {
    const editor = this.dependentProfileEditors[index]
    if (!editor) return
    if (editor.id) {
      this.pendingDelistIds.push(editor.id)
    }
    this.dependentProfileEditors.splice(index, 1)
  }

  @action.bound
  setTab(tab: ModalTab) {
    this.activeTab = tab
  }

  @computed
  get availableTabs(): ModalTab[] {
    const tabs: ModalTab[] = []
    if (this.personalProfileVM.personalInfoSupportingItem) {
      tabs.push('personal')
    }
    if (this.spousalProfileVM.spousalInfoSupportingItem) {
      tabs.push('spousal')
    }
    if (this.dependentProfileVM.dependentInfoSupportingItem) {
      tabs.push('dependents')
    }
    return tabs
  }

  @computed
  get isFirstTab() {
    return this.activeTab === this.availableTabs[0]
  }

  @computed
  get isLastTab() {
    return (
      this.activeTab === this.availableTabs[this.availableTabs.length - 1]
    )
  }

  @computed
  get nextTab(): ModalTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index >= 0 && index < this.availableTabs.length - 1) {
      return this.availableTabs[index + 1]
    }
    return null
  }

  @computed
  get previousTab(): ModalTab | null {
    const index = this.availableTabs.indexOf(this.activeTab)
    if (index > 0) {
      return this.availableTabs[index - 1]
    }
    return null
  }

  @action.bound
  skipCurrentTab() {
    if (this.nextTab) {
      this.activeTab = this.nextTab
      return
    }

    this.close()
  }

  @action.bound
  goToPreviousTab() {
    if (this.previousTab) {
      this.activeTab = this.previousTab
    }
  }

  @action
  reset() {
    this.personalProfileEditorVm.reset(undefined)
    this.spousalProfileEditorVm.reset(undefined)
    this.dependentProfileEditors = []
    this.pendingDelistIds = []
    this.activeTab = 'personal'
  }

  @override
  show(initialTab: ModalTab = 'personal') {
    this.personalProfileEditorVm.reset(
      this.personalProfileVM.profile,
    )
    this.spousalProfileEditorVm.reset(
      this.spousalProfileVM.profile,
    )

    this.dependentProfileEditors = []
    this.pendingDelistIds = []
    for (const dependent of this.dependentProfileVM.dependentProfiles) {
      if (!dependent) continue
      const editor = new DependentProfileEditorViewModel()
      editor.reset(dependent)
      this.dependentProfileEditors.push(editor)
    }

    this.activeTab = this.availableTabs.includes(initialTab)
      ? initialTab
      : this.availableTabs[0] ?? 'personal'

    super.show()
  }

  submit = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    try {
      switch (this.activeTab) {
        case 'personal':
          await this._savePersonal()
          break
        case 'spousal':
          await this._saveSpousal()
          break
        case 'dependents':
          await this._saveDependents()
          break
      }

      const nextTab = this.nextTab
      if (nextTab) {
        runInAction(() => {
          this.activeTab = nextTab
        })
      } else {
        this.close()
      }
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })

  private async _savePersonal() {
    const editor = this.personalProfileEditorVm
    const saved = await this.individualProfileStore.savePersonalProfile({
      workspaceId: this.job.workspace.id,
      ownerUserId: this.job.championClient!.member.user.publicId,
      occupation: editor.occupation,
      personalInfo: editor.personalInfoVm.toPersonalInfo(),
      contactInfo: editor.contactInfoVm.toContactInfo(),
    })

    await this.infoGatheringStore.provideSupportingItemPersonalInfo({
      jobId: this.job.id,
      itemId: this.personalProfileVM.personalInfoSupportingItem!.id,
      profileId: saved.id,
      profileVersion: saved.version,
    })
  }

  private async _saveSpousal() {
    const editor = this.spousalProfileEditorVm
    const saved = await this.individualProfileStore.saveSpousalProfile({
      workspaceId: this.job.workspace.id,
      ownerUserId: this.job.championClient!.member.user.publicId,
      occupation: editor.occupation,
      personalInfo: editor.personalInfoVm.toPersonalInfo(),
      contactInfo: {
        email: editor.email,
        phoneNumber: '',
        address: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          region: '',
          postalCode: '',
          country: '',
        },
      },
    })

    await this.infoGatheringStore.provideSupportingItemSpousalInfo({
      jobId: this.job.id,
      itemId: this.spousalProfileVM.spousalInfoSupportingItem!.id,
      profileId: saved.id,
      profileVersion: saved.version,
    })
  }

  private async _saveDependents() {
    for (const id of this.pendingDelistIds) {
      await this.infoGatheringStore.removeSupportingItemDependentInfo({
        jobId: this.job.id,
        itemId: this.dependentProfileVM.dependentInfoSupportingItem!.id,
        profileId: id,
      })
    }

    for (const editor of this.dependentProfileEditors) {
      if (editor.id && !editor.isDirty) {
        continue
      }

      const personalInfo = editor.personalInfoVm.toPersonalInfo()

      const saved = editor.id
        ? await this.individualProfileStore.updateDependentProfile({
            id: editor.id,
            workspaceId: this.job.workspace.id,
            ownerUserId: this.job.championClient!.member.user.publicId,
            relationship: editor.relationship,
            personalInfo,
          })
        : await this.individualProfileStore.createDependentProfile({
            workspaceId: this.job.workspace.id,
            ownerUserId: this.job.championClient!.member.user.publicId,
            relationship: editor.relationship,
            personalInfo,
          })

      runInAction(() => {
        editor.id = saved.id
        editor.version = saved.version
      })

      await this.infoGatheringStore.provideSupportingItemDependentInfo({
        jobId: this.job.id,
        itemId: this.dependentProfileVM.dependentInfoSupportingItem!.id,
        profileId: saved.id,
        profileVersion: saved.version,
      })
    }

    runInAction(() => {
      this.pendingDelistIds = []
    })
  }
}
